<template>
  <v-dialog
    v-model="dialog"
    max-width="1100"
  >
    <v-card>
      <v-card-title>
        <v-row pt-4>
          <v-file-input
            v-model="file"
            accept=".csv"
            label="Upload .csv file with payin fees"
          />
        </v-row>
      </v-card-title>

      <v-card-text v-if="preview">
        <div
          class="d-flex flex-wrap mb-3"
          style="gap: 8px;"
        >
          <v-chip
            small
            color="green"
            text-color="white"
          >
            {{ newCount }} new key(s)
          </v-chip>
          <v-chip
            small
            color="orange"
            text-color="white"
          >
            {{ changedCount }} changed key(s)
          </v-chip>
          <v-chip
            small
            color="grey"
            text-color="white"
          >
            {{ unchangedCount }} unchanged key(s)
          </v-chip>
          <v-chip
            small
            outlined
          >
            {{ preview.rowsToCreate }} row(s) to create
          </v-chip>
          <v-chip
            small
            outlined
          >
            {{ preview.rowsToArchive }} existing row(s) to archive
          </v-chip>
          <v-chip
            small
            :color="hasErrors ? 'red' : 'grey'"
            text-color="white"
          >
            {{ preview.invalidRows }} invalid row(s)
          </v-chip>
        </div>

        <v-alert
          v-if="hasErrors"
          type="error"
          dense
          text
        >
          <div class="mb-1">
            This file has invalid rows and will be rejected. Fix them and preview again:
          </div>
          <ul class="mb-0">
            <li
              v-for="(err, i) in preview.errors.slice(0, 50)"
              :key="i"
            >
              <span v-if="err.line">Row {{ err.line }}: </span>{{ err.message }}
            </li>
          </ul>
          <div v-if="preview.errors.length > 50">
            …and {{ preview.errors.length - 50 }} more.
          </div>
        </v-alert>

        <v-alert
          v-else-if="changedCount > 0 || newCount > 0"
          type="warning"
          dense
          text
        >
          Uploading replaces every tier of each key in the file.
          <strong>{{ newCount + changedCount }}</strong> key(s) will change
          <span v-if="unchangedCount"> ({{ unchangedCount }} are identical to what is already stored)</span>.
        </v-alert>
        <v-alert
          v-else
          type="info"
          dense
          text
        >
          Every key in this file is identical to what is already stored — uploading changes nothing.
        </v-alert>

        <v-data-table
          v-if="enrichedKeys.length"
          :headers="keyHeaders"
          :items="enrichedKeys"
          dense
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, 500, -1] }"
          class="elevation-1"
        >
          <template #[`item.key`]="{ item }">
            {{ keyLabel(item) }}
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip
              x-small
              :color="statusColor(item.status)"
              text-color="white"
            >
              {{ item.status }}
            </v-chip>
          </template>
          <template #[`item.currentTiers`]="{ item }">
            <div
              v-if="!item.currentTiers || !item.currentTiers.length"
              class="grey--text"
            >
              —
            </div>
            <div
              v-for="(t, i) in item.currentTiers"
              :key="i"
              class="grey--text text--darken-1"
            >
              {{ tierLine(t) }}
            </div>
          </template>
          <template #[`item.newTiers`]="{ item }">
            <div
              v-for="(t, i) in item.newTiers"
              :key="i"
              :class="{ 'green--text text--darken-2': item.status !== 'UNCHANGED' }"
            >
              {{ tierLine(t) }}
            </div>
          </template>
        </v-data-table>
        <div class="grey--text mt-2 caption">
          {{ enrichedKeys.length }} key(s). Tiers are shown as <strong>min amount → fee</strong>
          (a <em>%</em> fee is a percentage; otherwise it is a flat amount).
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn @click="close">
          Cancel
        </v-btn>
        <v-btn
          v-if="!preview"
          color="secondary"
          :loading="loadingPreview"
          :disabled="!isFileCorrect"
          @click="doPreview"
        >
          Preview changes
          <v-icon right>
            mdi-eye
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          color="primary"
          :loading="loadingUpload"
          :disabled="hasErrors"
          :dark="!hasErrors"
          @click="uploadFees"
        >
          Confirm upload
          <v-icon right>
            mdi-upload
          </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-overlay :value="loadingUpload">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>

    <UploadRateDialog
      :show-rate-dialog="showSuccessFileUpload"
      @on-close-dialog="onCloseSuccessDialog"
    />
  </v-dialog>
</template>

<script>
import UploadRateDialog from "@/components/dialogs/UploadRateDialog";

const STATUS_RANK = { NEW: 0, CHANGED: 1, UNCHANGED: 2 };

export default {
  name: "UploadPayinFeeModal",
  components: {
    UploadRateDialog
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      file: null,
      loadingPreview: false,
      loadingUpload: false,
      preview: null,
      showSuccessFileUpload: false,
      keyHeaders: [
        { text: "Key", value: "key", sortable: false },
        { text: "Change", value: "status", sortable: false },
        { text: "Current tiers (archived)", value: "currentTiers", sortable: false },
        { text: "New tiers (created)", value: "newTiers", sortable: false }
      ]
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    isFileCorrect() {
      return typeof this.file?.name === "string";
    },
    hasErrors() {
      return !!this.preview && this.preview.invalidRows > 0;
    },
    enrichedKeys() {
      return (this.preview?.keys || [])
        .map(k => ({ ...k, status: this.keyStatus(k) }))
        .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
    },
    newCount() {
      return this.enrichedKeys.filter(k => k.status === "NEW").length;
    },
    changedCount() {
      return this.enrichedKeys.filter(k => k.status === "CHANGED").length;
    },
    unchangedCount() {
      return this.enrichedKeys.filter(k => k.status === "UNCHANGED").length;
    }
  },
  watch: {
    file() {
      this.preview = null;
    }
  },
  methods: {
    async doPreview() {
      try {
        this.loadingPreview = true;
        this.preview = await this.$store.dispatch("PayinFeesStore/previewFees", this.getFormData());
      } finally {
        this.loadingPreview = false;
      }
    },
    async uploadFees() {
      try {
        this.loadingUpload = true;
        await this.$store.dispatch("PayinFeesStore/uploadFees", this.getFormData());
        this.close();
        this.showSuccessFileUpload = true;
        this.$emit("upload-success");
      } finally {
        this.loadingUpload = false;
      }
    },
    getFormData() {
      let formData = new FormData();
      formData.append("feesFile", this.file, this.file.name);
      return formData;
    },
    keyStatus(k) {
      if (k.changeType === "NEW") return "NEW";
      return this.tiersKey(k.currentTiers) === this.tiersKey(k.newTiers) ? "UNCHANGED" : "CHANGED";
    },
    tiersKey(tiers) {
      return (tiers || [])
        .map(t => `${t.minAmount}|${t.value}|${t.type}`)
        .sort()
        .join(";");
    },
    statusColor(status) {
      if (status === "NEW") return "green";
      if (status === "CHANGED") return "orange";
      return "grey";
    },
    keyLabel(k) {
      const source = [k.sourceCountryCode, k.sourceCurrencyCode].filter(Boolean).join("/");
      const meta = [k.payInMethod, k.paymentProvider].filter(Boolean).join(" / ");
      return `${source}${meta ? " [" + meta + "]" : ""}`;
    },
    tierLine(t) {
      const fee = t.type === "percentage" ? `${t.value}%` : `${t.value} flat`;
      return `min ${t.minAmount} → ${fee}`;
    },
    close() {
      this.dialog = false;
      this.preview = null;
      this.file = null;
    },
    onCloseSuccessDialog() {
      this.showSuccessFileUpload = false;
      this.close();
    }
  }
};
</script>

<style scoped>

</style>
